import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { Switch } from 'components/ui/switch';
import { SegmentedTabs } from 'components/ui/data-table';
import { cn } from 'lib/utils';
import { useLeaderboard, useLeaderboardVisibility, type LeaderboardExam } from 'modules/leaderboard';
import { PortalHeading } from './PortalHeading';

const MEDAL = ['text-amber-500', 'text-slate-400', 'text-orange-700'];

const score = (exam: LeaderboardExam, value: number | null) =>
  value === null ? '—' : exam === 'IELTS' ? value.toFixed(1) : String(Math.round(value));

/** This week's IELTS or SAT practice board: tests finished since Monday, ties by best score. */
export default function Leaderboard() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const exam: LeaderboardExam = params.get('exam') === 'SAT' ? 'SAT' : 'IELTS';
  const { data, isLoading, isError, refetch } = useLeaderboard(exam);
  const visibility = useLeaderboardVisibility();
  const hidden = visibility.isPending ? Boolean(visibility.variables) : Boolean(data?.hidden);

  return (
    <div className="flex flex-col">
      <PortalHeading title={t('leaderboard.title')} description={t('leaderboard.subtitle')} />
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 md:mt-5">
        <SegmentedTabs<LeaderboardExam>
          aria-label={t('leaderboard.exam')}
          className="h-9"
          options={[{ value: 'IELTS' as const, label: 'IELTS' }, { value: 'SAT' as const, label: 'SAT' }]}
          value={exam}
          onChange={(next) => setParams(next === 'SAT' ? { exam: 'SAT' } : {}, { replace: true })}
        />
        <Switch
          size="sm"
          checked={!hidden}
          disabled={!data || visibility.isPending}
          onChange={(shown) => visibility.mutate(!shown)}
          label={t('leaderboard.show_me')}
        />
      </div>

      {data?.me && (
        <p className="mt-4 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-foreground">
          {data.me.rank && !hidden
            ? t('leaderboard.my_rank', { rank: data.me.rank, count: data.me.tests })
            : t('leaderboard.my_tests', { count: data.me.tests })}
        </p>
      )}

      <section className="mt-4 overflow-hidden rounded-lg border border-border bg-card">
        {isLoading ? (
          <div className="space-y-3 p-5" aria-hidden>
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : isError ? (
          <EmptyState icon={<Trophy />} title={t('leaderboard.load_failed')} secondaryAction={{ label: t('retry'), onClick: () => void refetch() }} />
        ) : !data?.entries.length ? (
          <EmptyState icon={<Trophy />} title={t('leaderboard.empty_title')} description={t('leaderboard.empty_body')} />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-[12px] text-muted-foreground">
              <tr>
                <th scope="col" className="w-14 px-4 py-2.5 font-medium">#</th>
                <th scope="col" className="px-2 py-2.5 font-medium">{t('leaderboard.learner')}</th>
                <th scope="col" className="px-2 py-2.5 text-right font-medium">{t('leaderboard.tests')}</th>
                <th scope="col" className="px-4 py-2.5 text-right font-medium">{t(exam === 'IELTS' ? 'leaderboard.best_band' : 'leaderboard.best_score')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.entries.map((entry) => (
                <tr key={entry.rank} className={cn(entry.isMe && 'bg-primary/10')} aria-current={entry.isMe ? 'true' : undefined}>
                  <td className="px-4 py-3 font-semibold tabular-nums">
                    {entry.rank <= 3 ? <Trophy className={cn('h-4 w-4', MEDAL[entry.rank - 1])} aria-label={String(entry.rank)} /> : entry.rank}
                  </td>
                  <td className="px-2 py-3 font-medium text-foreground">
                    {entry.name}
                    {entry.isMe && <span className="ml-1.5 text-xs text-muted-foreground">({t('leaderboard.you')})</span>}
                  </td>
                  <td className="px-2 py-3 text-right tabular-nums">{entry.tests}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{score(exam, entry.bestScore)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
